import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Quote, ArrowRight } from "lucide-react";

const BANNER = "/zio-camillo-banner.jpg";
const PORTRAIT = "/zio-camillo-insegnamento.jpg";

const VALUE_KEYS = ["cura", "umilta", "passione"];

const ZioCamillo = () => {
  const { t } = useTranslation();
  return (
    <PageShell>
      <SeoHead pageKey="camillo" image={BANNER} />
      <PageBanner
        eyebrow={t("camillo.banner.eyebrow")}
        title={t("camillo.banner.title")}
        subtitle={t("camillo.banner.subtitle")}
        image={BANNER}
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
            {t("camillo.eyebrow")}
          </div>
          <p className="text-lg sm:text-xl leading-relaxed text-center" style={{ color: "#3b2417" }}>
            {t("camillo.intro1")}<br />
            <strong>{t("camillo.intro2")}</strong>
          </p>

          {/* Historical founder photo — Zio Camillo alongside the vintage roaster */}
          <figure
            className="mt-12 sm:mt-14 mx-auto w-full max-w-[760px]"
            data-testid="zio-camillo-historical-photo"
          >
            <div
              className="relative overflow-hidden rounded-xl sm:rounded-2xl"
              style={{
                border: "1px solid rgba(91,54,33,0.15)",
                boxShadow: "0 30px 60px -25px rgba(42,22,12,0.45), 0 12px 24px -10px rgba(42,22,12,0.25)",
              }}
            >
              <img
                src="/zio-camillo-photo.jpg"
                alt="Zio Camillo Cola accanto alla storica torrefattrice artigianale"
                className="block w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <figcaption
              className="mt-3 text-center text-xs sm:text-sm italic"
              style={{ color: "#7a4a2e" }}
            >
              Zio Camillo Cola davanti alla sua storica torrefattrice
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section-y relative overflow-hidden" style={{ background: "#2a160c", color: "#f5ead6" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div
            className="relative rounded-3xl p-10 sm:p-14 lg:p-20 text-center overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #3b2417 0%, #2a160c 100%)",
              border: "1px solid rgba(255,245,236,0.1)",
            }}
          >
            <Quote size={64} style={{ color: "#c5371f" }} className="mx-auto opacity-80" />
            <blockquote
              className="font-serif-display font-semibold italic mt-6 leading-tight"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.6rem)", color: "#f5ead6" }}
            >
              {t("camillo.quote")}
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-3">
              <span className="h-px w-16" style={{ background: "#c5371f" }} />
              <cite className="not-italic font-serif-display uppercase tracking-[0.25em] text-xs" style={{ color: "#c5371f" }}>
                {t("camillo.quoteAuthor")}
              </cite>
              <span className="h-px w-16" style={{ background: "#c5371f" }} />
            </div>
            <p className="mt-6 text-sm" style={{ color: "#ecdcbd" }}>{t("camillo.quoteFooter")}</p>
          </div>
        </div>
      </section>

      <section className="section-y" style={{ background: "#f5ead6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img src={PORTRAIT} alt={t("camillo.portraitAlt")} className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(42,22,12,0.75) 100%)" }} />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="inline-block px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ background: "#c5371f", color: "#fff" }}>
                  {t("camillo.storyBadge")}
                </span>
                <p className="font-serif-display text-white text-xl italic mt-3">{t("camillo.storyImageCaption")}</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#c5371f" }}>{t("camillo.storyEyebrow")}</span>
            <h2 className="font-serif-display font-bold mt-3 leading-tight" style={{ color: "#2a160c", fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}>
              {t("camillo.storyTitle")}
            </h2>
            <div className="mt-6 space-y-4 text-[15px] sm:text-base leading-relaxed" style={{ color: "#3b2417" }}>
              <p dangerouslySetInnerHTML={{ __html: t("camillo.storyP1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("camillo.storyP2") }} />
              <p dangerouslySetInnerHTML={{ __html: t("camillo.storyP3") }} />
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {VALUE_KEYS.map((k) => (
                <div key={k} className="rounded-xl p-4 text-center" style={{ background: "#fbf5e9", border: "1px solid rgba(91,54,33,0.15)" }}>
                  <div className="font-serif-display text-lg font-bold" style={{ color: "#c5371f" }}>{t(`camillo.values.${k}.title`)}</div>
                  <div className="text-[11px] uppercase tracking-wider mt-1" style={{ color: "#5b3621" }}>{t(`camillo.values.${k}.desc`)}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link to="/le-origini" className="btn-red focus-ring inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold uppercase tracking-wider text-sm">
                {t("camillo.cta")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ZioCamillo;
